import { useMemo } from "react";
import { parseDate } from "./utils";
import { itemMatchesDueFilter } from "@/components/DueFilterPopover";

export function useStageItems({
  items, stage, searchQuery, globalFilter,
  showDueThisWeekOnly, showReworkOnly, showBlockedOnly, showQcPendingOnly,
  procSubteamFilter, dueThisWeekDays, mfgStatusFilter, qcStatusFilter,
  dispatchStatusFilter, routerPendingOnly, kittingPendingOnly, qdPendingOnly,
  caAcceptPendingOnly, dueFilter, dueSort, shippedAtFilter, shippedAtSort,
}) {
  // Pre-bucket items by stage ONCE per items change (perf, 17-Jul) — a filter
  // keystroke no longer re-scans all ~1200 items; it starts from this stage's
  // bucket. Stable base ref per stage until the data changes.
  const byStage = useMemo(() => {
    const m = new Map();
    for (const i of items) {
      const arr = m.get(i.stage);
      if (arr) arr.push(i); else m.set(i.stage, [i]);
    }
    return m;
  }, [items]);
  return useMemo(() => {
    // .slice() — the sort() below mutates in place; never mutate the cached bucket.
    let filtered = (byStage.get(stage) || []).slice();
    if (globalFilter.clients) filtered = filtered.filter(i => globalFilter.clients.includes(i.client));
    if (globalFilter.priority !== "all") filtered = filtered.filter(i => i.priority === globalFilter.priority);
    if (globalFilter.flag === "blocked") filtered = filtered.filter(i => i.is_blocked);
    else if (globalFilter.flag === "overdue") {
      const _t = new Date(); _t.setHours(0, 0, 0, 0);
      filtered = filtered.filter(i => { const d = parseDate(i.dispatch_due_date || i.due_date); return d && d < _t; });
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter(i =>
        i.item_description?.toLowerCase().includes(q) ||
        i.po_number?.toLowerCase().includes(q) ||
        i.part_number?.toLowerCase().includes(q) ||
        i.client?.toLowerCase().includes(q) ||
        i.vendor?.toLowerCase().includes(q)
      );
    }
    if (showDueThisWeekOnly) {
      const today = new Date(); today.setHours(0,0,0,0);
      const nextWeek = new Date(today); nextWeek.setDate(today.getDate() + dueThisWeekDays);
      filtered = filtered.filter(i => {
        const d = parseDate(i.dispatch_due_date || i.due_date);
        return d && d >= today && d <= nextWeek;
      });
    }
    if (showReworkOnly) filtered = filtered.filter(i => i.is_rework);
    if (showBlockedOnly) filtered = filtered.filter(i => i.is_blocked);
    if (stage === "Quality Control" && showQcPendingOnly) {
      filtered = filtered.filter(i => (i.qc_status || "Pending Inspection") === "Pending Inspection");
    }
    if (stage === "Manufacturing" && mfgStatusFilter.length) {
      const set = new Set(mfgStatusFilter);
      filtered = filtered.filter(i => {
        const status = i.manufacturing_status || "Queued";
        if (set.has("Queued") && status === "Queued") return !!i.kitting_complete;
        return set.has(status);
      });
    }
    if (stage === "Manufacturing" && routerPendingOnly) {
      filtered = filtered.filter(i => i.mfg_gate_version === "v1" && !i.router_card_number);
    }
    if (stage === "Manufacturing" && kittingPendingOnly) {
      filtered = filtered.filter(i => i.mfg_gate_version === "v1" && !i.kitting_complete);
    }
    if (stage === "Quality Control" && qcStatusFilter.length) {
      const set = new Set(qcStatusFilter);
      filtered = filtered.filter(i => set.has(i.qc_status || "Pending Inspection"));
    }
    if (stage === "Quality Control" && qdPendingOnly) {
      filtered = filtered.filter(i => (i.qc_status === "Passed") && !i.quality_dossier_ready);
    }
    if (stage === "Quality Control" && caAcceptPendingOnly) {
      filtered = filtered.filter(i => i.quality_dossier_ready && !i.customer_acceptance);
    }
    if (stage === "Dispatch" && dispatchStatusFilter.length) {
      const set = new Set(dispatchStatusFilter);
      filtered = filtered.filter(i => set.has(i.dispatch_status || "Pending"));
    }
    if (stage === "Procurement" && procSubteamFilter) {
      const fieldMap = { material: "material_status", tooling: "tooling_status", gauging: "gauging_status" };
      const f = fieldMap[procSubteamFilter.team];
      const targetStatus = procSubteamFilter.status;
      if (f && targetStatus) {
        filtered = filtered.filter(i => {
          const cur = (i[f] || "Pending");
          // Treat "Partially Received" as part of "Ordered" bucket (mirrors the
          // existing progress-bar grouping where Ordered shows both).
          if (targetStatus === "Ordered") return cur === "Ordered" || cur === "Partially Received";
          return cur === targetStatus;
        });
      }
    }
    if (dueFilter) {
      filtered = filtered.filter(i => itemMatchesDueFilter(i.dispatch_due_date || i.due_date, dueFilter));
    }
    if (shippedAtFilter && (stage === "Dispatch" || stage === "Completed")) {
      filtered = filtered.filter(i => itemMatchesDueFilter(i.shipped_at, shippedAtFilter));
    }
    const sorted = filtered.sort((a, b) => {
      if (shippedAtSort && (stage === "Dispatch" || stage === "Completed")) {
        const sa = a.shipped_at ? new Date(a.shipped_at) : null;
        const sb = b.shipped_at ? new Date(b.shipped_at) : null;
        if (!sa && !sb) return 0;
        if (!sa) return 1; if (!sb) return -1;
        return shippedAtSort === "desc" ? sb - sa : sa - sb;
      }
      const da = parseDate(a.dispatch_due_date || a.due_date);
      const db = parseDate(b.dispatch_due_date || b.due_date);
      if (!da) return 1; if (!db) return -1;
      return da - db;
    });
    if (dueSort === "desc") sorted.reverse();
    return sorted;
  }, [byStage, stage, searchQuery, globalFilter, showDueThisWeekOnly, showReworkOnly, showBlockedOnly, showQcPendingOnly, procSubteamFilter, dueThisWeekDays, mfgStatusFilter, qcStatusFilter, dispatchStatusFilter, routerPendingOnly, kittingPendingOnly, qdPendingOnly, caAcceptPendingOnly, dueFilter, dueSort, shippedAtFilter, shippedAtSort]);
}
